import React, { useEffect, useState } from 'react';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import moment from 'moment';
import PropTypes from 'prop-types';

import { DateRange as DateRangeRDR } from 'react-date-range';

import { useTranslation } from 'react-i18next';

// eslint-disable-next-line import/no-cycle
import { ClickOutside } from 'ui/atoms';
import theme from 'ui/theme';
import MainFont from 'ui/typography/mainFont';

import {
  WrapperDateRange,
  DateRangeValue,
  WrapperPicker,
  Icon,
  LabelWrapper,
  Title,
  Selection
} from './style';

const DateRange = ({
  placeholder,
  onChange,
  isClearable = false,
  onReset,
  selectEndDateError,
  minDate,
  maxSixMonths,
  values
}) => {
  const [ranges, setRanges] = useState([
    {
      startDate: null,
      endDate: new Date(''),
      key: 'selection'
    }
  ]);
  const [showPicker, setShowPicker] = useState(false);
  const { t } = useTranslation();
  const rangeStartDate = ranges?.[0]?.startDate;
  const rangeEndDate = ranges?.[0]?.endDate;
  const [endDateNotSelectedError, setEndDateNotSelectedError] = useState(false);
  const [borders, setBorders] = useState({
    max: new Date(),
    min:
      minDate ||
      moment(new Date())
        .subtract(12, 'months')
        .toDate()
  });

  useEffect(() => {
    setEndDateNotSelectedError(selectEndDateError);
  }, [selectEndDateError]);

  const handleChange = ({ startDate, endDate }) => {
    let start;

    if (
      startDate &&
      moment(startDate).toISOString(true) ===
        moment(endDate).toISOString(true) &&
      ranges?.[0]?.startDate
    ) {
      if (
        moment(ranges?.[0]?.startDate).toISOString(true) !==
        moment(ranges?.[0]?.endDate).toISOString(true)
      ) {
        start = startDate;
      } else {
        start = ranges?.[0]?.startDate;
      }
    } else if (!startDate && endDate) {
      start = endDate;
    }

    setRanges([
      {
        startDate: start || startDate,
        endDate,
        key: 'selection'
      }
    ]);
  };

  useEffect(() => {
    if (rangeStartDate && rangeEndDate) {
      if (moment(rangeStartDate).unix() > moment(rangeEndDate).unix()) {
        ranges[0].startDate = ranges[0].endDate;
        ranges[0].endDate = rangeStartDate;
      }
      if (rangeStartDate && onChange && typeof onChange === 'function') {
        onChange([
          moment(rangeStartDate).toISOString(true),
          moment(rangeEndDate).toISOString(true)
        ]);
        if (moment(rangeEndDate).unix() !== moment(rangeStartDate).unix()) {
          if (endDateNotSelectedError) {
            setEndDateNotSelectedError(false);
          }
          setShowPicker(false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStartDate, rangeEndDate]);

  useEffect(() => {
    if (maxSixMonths) {
      const newBorders = {
        max: new Date(),
        min:
          minDate ||
          moment(new Date())
            .subtract(12, 'months')
            .toDate()
      };
      if (moment(ranges[0].startDate).isSame(moment(ranges[0].endDate))) {
        const newMin = moment(ranges[0].startDate).subtract(6, 'months');
        const newMax = moment(ranges[0].startDate).add(6, 'months');
        const minBorder = moment(borders.min);
        if (newMin.isAfter(minBorder)) {
          newBorders.min = newMin.toDate();
        }
        const maxBorder = moment(borders.max);
        if (newMax.isBefore(maxBorder)) {
          newBorders.max = newMax.toDate();
        }
        setBorders(newBorders);
      } else {
        setBorders(newBorders);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ranges]);

  useEffect(() => {
    if (Array.isArray(values) && !ranges[0].startDate) {
      setRanges([
        {
          startDate: values[0],
          endDate: values[1],
          key: 'selection'
        }
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetPeriod = () => {
    setRanges([
      {
        startDate: null,
        endDate: new Date(''),
        key: 'selection'
      }
    ]);
    if (onReset) {
      onReset('period', setRanges);
    }
  };

  return (
    <ClickOutside callback={() => setShowPicker(false)}>
      <WrapperDateRange>
        <DateRangeValue onClick={() => setShowPicker(!showPicker)}>
          <LabelWrapper>
            <Title datePresent={!!ranges[0]?.startDate}>
              <MainFont configuration={{ fontSize: 14 }}>
                {placeholder}
              </MainFont>
            </Title>
            {!!ranges[0]?.startDate && (
              <Selection
                datePresent={!!ranges[0]?.startDate}
                endDateNotSelectedError={endDateNotSelectedError}
              >
                <MainFont configuration={{ fontSize: 12, fontWeight: 600 }}>
                  {`${ranges
                    .map(
                      el =>
                        `${moment(el.startDate).format('DD/MM/YYYY')} —  ${
                          moment(el.endDate).isValid() &&
                          !moment(el.endDate).isSame(moment(el.startDate))
                            ? moment(el.endDate).format('DD/MM/YYYY')
                            : ''
                        }`
                    )
                    .join('')}`}
                </MainFont>
              </Selection>
            )}
          </LabelWrapper>
          <Icon className="icon-chevron-up" isOpen={showPicker} />
        </DateRangeValue>

        {showPicker && (
          <WrapperPicker>
            {isClearable && (
              <p
                onClick={() => {
                  if (ranges?.[0].startDate) resetPeriod();
                }}
              >
                {t('reset_filter')}
              </p>
            )}
            <DateRangeRDR
              ranges={ranges}
              showMonthAndYearPickers={false}
              showDateDisplay={false}
              maxDate={borders.max}
              minDate={borders.min}
              showPreview={false}
              moveRangeOnFirstSelection={false}
              color={theme().colors.pink.three}
              classNames={{
                nextPrevButton: 'icon-chevron-up nextPrevButtonCustom'
              }}
              onChange={({ selection }) => {
                handleChange(selection);
              }}
            />
          </WrapperPicker>
        )}
      </WrapperDateRange>
    </ClickOutside>
  );
};

DateRange.defaultProps = {
  selectEndDateError: false
};

DateRange.propTypes = {
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  isClearable: PropTypes.bool,
  onReset: PropTypes.func,
  selectEndDateError: PropTypes.bool,
  minDate: PropTypes.object,
  maxSixMonths: PropTypes.bool,
  values: PropTypes.array
};

export default DateRange;
